import { useCallback, useEffect, useRef, useState } from "react";
import type { PlatformTypeName } from "@multi-chat/shared";
import { getOrganization } from "../../api/organizations-api.js";

export interface OrganizationPlatformFlags {
  whatsappEnabled: boolean;
  facebookEnabled: boolean;
  instagramEnabled: boolean;
}

export interface OrganizationPlatformFlagState {
  flags: OrganizationPlatformFlags | null;
  /** Increments after every successful load, so callers can tell a refreshed value from a stale one. */
  version: number;
  /** Aborts any in-flight read and loads the flags again. */
  refresh: () => void;
}

interface LoadedFlags {
  organizationId: number;
  flags: OrganizationPlatformFlags | null;
}

/**
 * Loads the three platform flags from the organization payload and reloads them
 * when the window regains focus or becomes visible.
 * Flags are null while loading or if the read fails. Callers leave controls usable
 * until a flag is known false; the API still rejects the action.
 */
export function useOrganizationPlatformFlagState(
  organizationId: number | null,
): OrganizationPlatformFlagState {
  const [loaded, setLoaded] = useState<LoadedFlags | null>(null);
  const [version, setVersion] = useState(0);
  const [reloadToken, setReloadToken] = useState(0);
  const controllerRef = useRef<AbortController | null>(null);

  useEffect(() => {
    if (organizationId == null) {
      return;
    }

    const controller = new AbortController();
    controllerRef.current = controller;
    getOrganization(organizationId, controller.signal)
      .then((organization) => {
        if (controller.signal.aborted) return;
        setLoaded({
          organizationId,
          flags: {
            whatsappEnabled: organization.whatsappEnabled,
            facebookEnabled: organization.facebookEnabled,
            instagramEnabled: organization.instagramEnabled,
          },
        });
        setVersion((current) => current + 1);
      })
      .catch(() => {
        if (controller.signal.aborted) return;
        setLoaded({ organizationId, flags: null });
      })
      .finally(() => {
        if (controllerRef.current === controller) controllerRef.current = null;
      });

    return () => {
      controller.abort();
      if (controllerRef.current === controller) controllerRef.current = null;
    };
  }, [organizationId, reloadToken]);

  const refresh = useCallback(() => {
    controllerRef.current?.abort();
    controllerRef.current = null;
    setReloadToken((token) => token + 1);
  }, []);

  useEffect(() => {
    if (organizationId == null) return;

    const reloadWhenVisible = (): void => {
      if (document.visibilityState !== "visible") return;
      if (controllerRef.current != null) return;
      setReloadToken((token) => token + 1);
    };

    window.addEventListener("focus", reloadWhenVisible);
    document.addEventListener("visibilitychange", reloadWhenVisible);
    return () => {
      window.removeEventListener("focus", reloadWhenVisible);
      document.removeEventListener("visibilitychange", reloadWhenVisible);
    };
  }, [organizationId]);

  const flags =
    organizationId != null && loaded?.organizationId === organizationId ? loaded.flags : null;

  return { flags, version, refresh };
}

export function useOrganizationPlatformFlags(
  organizationId: number | null,
): OrganizationPlatformFlags | null {
  return useOrganizationPlatformFlagState(organizationId).flags;
}

export function isPlatformEntitlementEnabled(
  flags: OrganizationPlatformFlags | null,
  platform: PlatformTypeName,
): boolean | null {
  if (flags == null) return null;
  switch (platform) {
    case "WHATSAPP":
      return flags.whatsappEnabled;
    case "FACEBOOK":
      return flags.facebookEnabled;
    case "INSTAGRAM":
      return flags.instagramEnabled;
    default: {
      const exhaustive: never = platform;
      return exhaustive;
    }
  }
}
